/**
 * CANONICAL NOTIFICATION MODEL
 * ----------------------------
 * One model drives creation, storage, lifecycle, filtering, counts and the
 * bell badge. Every surface (notification center, header bell, dashboard)
 * reads notifications through this module — no surface may derive state,
 * category, or counts from message text, workflow status, or tone.
 *
 * Storage: public.notifications
 *   id, user_id, kind, category, title, body, status (delivery),
 *   read_at, dismissed_at, archived_at, created_at,
 *   review_id, negotiation_id, report_id, billing_record_id
 */
import type { Notification } from "@/lib/db/models";

/* --------------------------------- category -------------------------------- */

export const NOTIFICATION_CATEGORIES = [
  "review",
  "negotiation",
  "billing",
  "payment",
  "report",
  "system",
  "security",
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

const REPORT_KINDS = new Set(["report_ready", "report_delivered", "report_updated"]);
const PAYMENT_KINDS = new Set([
  "payment_received",
  "invoice_paid",
  "billing_paid",
  "payment_failed",
]);
const BILLING_KINDS = new Set(["billing", "billing_failed", "billing_receipt", "refund_issued"]);
const SECURITY_KINDS = new Set(["security_alert", "login_alert"]);
const REVIEW_KINDS = new Set([
  "review_submitted",
  "review_started",
  "review_completed",
  "ai_review_started",
  "ai_processing_failed",
  "files_uploaded",
  "new_review",
  "documents_required",
  "additional_documentation_required",
  "action_required",
]);

/** Canonical category for a notification kind. Mirrors the DB backfill rule. */
export function categoryForKind(kind: string): NotificationCategory {
  if (REPORT_KINDS.has(kind)) return "report";
  if (kind.startsWith("negotiation")) return "negotiation";
  if (PAYMENT_KINDS.has(kind)) return "payment";
  if (BILLING_KINDS.has(kind)) return "billing";
  if (SECURITY_KINDS.has(kind)) return "security";
  if (REVIEW_KINDS.has(kind)) return "review";
  return "system";
}

/** Stored category wins; kind is only a fallback for legacy rows. */
export function notificationCategory(n: Pick<Notification, "kind" | "category">): NotificationCategory {
  const stored = n.category as NotificationCategory | null | undefined;
  if (stored && (NOTIFICATION_CATEGORIES as readonly string[]).includes(stored)) return stored;
  return categoryForKind(n.kind);
}

/* ---------------------------------- state ---------------------------------- */

export type NotificationState = "unread" | "read" | "dismissed" | "archived";

export type StateFields = {
  read_at: string | null;
  dismissed_at?: string | null;
  archived_at?: string | null;
};

/**
 * CANONICAL lifecycle state. Never derived from workflow status
 * (completed / report ready / negotiation complete / payment complete).
 */
export function notificationState(n: StateFields): NotificationState {
  if (n.archived_at) return "archived";
  if (n.dismissed_at) return "dismissed";
  return n.read_at == null ? "unread" : "read";
}

export function isRead(n: StateFields): boolean {
  return n.read_at != null;
}

export function isDismissed(n: StateFields): boolean {
  return Boolean(n.dismissed_at) || Boolean(n.archived_at);
}

/** Left status bar: unread RED, read GREEN, dismissed/archived GRAY. */
export function stateRailClass(n: StateFields): string {
  const s = notificationState(n);
  if (s === "unread") return "bg-destructive";
  if (s === "read") return "bg-savings";
  return "bg-muted-foreground/40";
}

export function stateLabel(n: StateFields): string {
  const s = notificationState(n);
  if (s === "unread") return "Unread notification";
  if (s === "read") return "Reviewed notification";
  if (s === "dismissed") return "Dismissed notification";
  return "Archived notification";
}

/* --------------------------------- filtering -------------------------------- */

export type CategoryFilter = "all" | "unread" | NotificationCategory;
export type DateFilter = "any" | "today" | "week" | "earlier";

/** Category/state filter — always applied FIRST. */
export function matchesCategoryFilter(n: Notification, filter: CategoryFilter): boolean {
  if (filter === "all") return true;
  if (filter === "unread") return !isRead(n);
  return notificationCategory(n) === filter;
}

/** Date filter — always applied AFTER the category filter. */
export function matchesDateFilter(n: Notification, filter: DateFilter, now = new Date()): boolean {
  if (filter === "any") return true;
  const t = new Date(n.created_at).getTime();
  if (Number.isNaN(t)) return filter === "earlier";
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (filter === "today") return t >= startOfToday;
  if (filter === "week") return t >= startOfToday - 6 * 86_400_000;
  return t < startOfToday - 6 * 86_400_000;
}

/**
 * The full pipeline the Notification Center uses:
 *   visible (non-dismissed) -> category -> date
 */
export function applyNotificationFilters(
  items: Notification[],
  category: CategoryFilter,
  date: DateFilter,
  now = new Date(),
): Notification[] {
  return items
    .filter((n) => !isDismissed(n))
    .filter((n) => matchesCategoryFilter(n, category))
    .filter((n) => matchesDateFilter(n, date, now));
}

/** Bell badge = COUNT(is_read = false) over non-dismissed notifications. */
export function unreadBadgeCount(items: Notification[]): number {
  return items.filter((n) => !isDismissed(n) && !isRead(n)).length;
}
